import { FormControl, FormErrorMessage, FormLabel } from '@chakra-ui/react'
import { isNull } from 'lodash'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { CMFieldProps } from '.'
import { IKReactWindowedSelect } from '../../../../../ui-kit'
import { TypeBadge } from '../../../../../utils/mediaLibrary'

const CMMultiSelectField = ({
  cmField,
  disabled,
  isUpload,
  form,
}: CMFieldProps): ReactElement => {
  const { t } = useTranslation()

  const formattedValue = useMemo(() => {
    if (!Array.isArray(form?.values?.upload[cmField.name]) || form?.values?.upload[cmField.name].length === 0) {
      return null
    }

    return form?.values?.upload[cmField.name].map(item => ({
      label: disabled ? String(item) : (
        <>
          {String(item)} <TypeBadge val={item} />
        </>
      ),
      value: item,
    }))
  }, [form?.values?.upload[cmField.name], disabled, cmField.name, cmField])

  const placeholder = useMemo(() => {
    if (
      Array.isArray(cmField.schema.defaultValue) &&
      cmField.schema.defaultValue.length > 0 &&
      isUpload
    ) {
      return `${cmField.schema.defaultValue
        .map(val => `${val}[${typeof val}]`)
        .join(', ')} (Default value)`
    }

    return ''
  }, [cmField.schema.defaultValue, isUpload, cmField])

  const onChange = useCallback(
    (newVal: Array<{ label: string; value: string }>) => {
      form.setTouched({ ...form.touched, [cmField.name]: true })
      if (isNull(newVal)) {
        form.setFieldValue(`upload.['${cmField.name}']`, null)
      } else {
        form.setFieldValue(`upload.['${cmField.name}']`, newVal.map(item => item.value))
      }
    },
    [form, cmField.name],
  )

  const options = useMemo(() => {
    return cmField.schema.selectOptions?.map(item => ({
      label: String(item),
      value: item,
    }))
  }, [cmField.schema.selectOptions])

  return (
    <FormControl
      marginBottom={isUpload ? 0 : '30px'}
      isInvalid={Boolean(
        form.errors?.upload &&
          form.touched?.upload &&
          form.errors?.upload[cmField.name] &&
          form.touched?.upload[cmField.name],
      )}
      isRequired={cmField.schema.isValueRequired}
      isDisabled={disabled}
    >
      <FormLabel>{cmField.label}</FormLabel>
      <IKReactWindowedSelect
        placeholder={
          placeholder || t('set_a_default_value', 'Set a default value')
        }
        variant="compact"
        captureMenuScroll={false}
        menuPlacement="auto"
        isClearable
        isMulti
        isDisabled={disabled}
        value={formattedValue}
        options={options}
        onChange={onChange}
      />
      <FormErrorMessage>
        {form.errors?.upload && form.errors?.upload[cmField.name]}
      </FormErrorMessage>
    </FormControl>
  )
}

export default CMMultiSelectField
